import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import { User as UserIcon, Mail, Briefcase, Phone, Hash, Building2, Lock, CheckCircle2 } from 'lucide-react';

export function Profile() {
  const { data: profile, isLoading } = useQuery({
    queryKey: ['employeeProfile'],
    queryFn: async () => {
      const res = await apiClient.get('/profile');
      return res.data.data;
    },
  });

  useEffect(() => {
    if (profile?.name) {
      localStorage.setItem('userName', profile.name);
      localStorage.setItem('isProfileComplete', 'true');
    }
  }, [profile]);

  return (
    <Layout title="My Profile">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* HR Master Sync Info Card */}
        {/* <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 p-4 rounded-2xl flex items-start space-x-3 shadow-sm">
          <Lock className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <h4 className="font-bold text-sm">Official Employee Master Registry Data</h4>
              <span className="inline-flex items-center space-x-1 bg-emerald-100 text-emerald-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">
                <CheckCircle2 className="w-3 h-3" />
                <span>Synced</span>
              </span>
            </div>
            <p className="text-xs text-emerald-700 mt-1 leading-relaxed">
              Your profile details are automatically matched and synced from the official HR Employee Master Registry. These details are read-only and cannot be manually modified.
            </p>
          </div>
        </div> */}

        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm flex items-start space-x-6">
          <div className="w-20 h-20 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center font-display text-blue-700 font-bold text-3xl shrink-0">
            {profile?.name?.charAt(0) || <UserIcon className="w-9 h-9" />}
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-black text-slate-800 tracking-tight">{profile?.name || 'Loading...'}</h2>
            <p className="text-slate-500 flex items-center space-x-2 mt-1 text-sm">
              <Mail className="w-4 h-4 text-slate-400" />
              <span>{profile?.email || '—'}</span>
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <p className="text-[11px] text-blue-700 font-extrabold uppercase tracking-wider px-3 py-1 bg-blue-50 border border-blue-100 inline-block rounded-full">
                {profile?.role || 'EMPLOYEE'}
              </p>
              {profile?.isEmployeeMasterMatched && (
                <span className="text-[11px] text-emerald-700 font-bold uppercase tracking-wider px-3 py-1 bg-emerald-50 border border-emerald-100 inline-block rounded-full">
                  HR Master Registry Verified
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm space-y-6">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <h3 className="text-base font-bold text-slate-800">Employee Master Details</h3>
            <span className="text-xs text-slate-400 font-medium flex items-center gap-1">
              <Lock className="w-3.5 h-3.5" /> Read-Only Field Set
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Full Name</label>
              <div className="relative">
                <UserIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={profile?.name || ''}
                  readOnly
                  disabled
                  className="w-full pl-9 pr-3 py-2.5 bg-slate-100/70 border border-slate-200 rounded-xl text-slate-800 text-sm font-semibold cursor-not-allowed select-none focus:outline-none"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">MP ID (Employee ID)</label>
              <div className="relative">
                <Hash className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={profile?.mpId || '—'}
                  readOnly
                  disabled
                  className="w-full pl-9 pr-3 py-2.5 bg-slate-100/70 border border-slate-200 rounded-xl text-slate-800 text-sm font-semibold cursor-not-allowed select-none focus:outline-none"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Department</label>
              <div className="relative">
                <Building2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={profile?.department || '—'}
                  readOnly
                  disabled
                  className="w-full pl-9 pr-3 py-2.5 bg-slate-100/70 border border-slate-200 rounded-xl text-slate-800 text-sm font-semibold cursor-not-allowed select-none focus:outline-none"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Designation</label>
              <div className="relative">
                <Briefcase className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={profile?.designation || '—'}
                  readOnly
                  disabled
                  className="w-full pl-9 pr-3 py-2.5 bg-slate-100/70 border border-slate-200 rounded-xl text-slate-800 text-sm font-semibold cursor-not-allowed select-none focus:outline-none"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Mobile Number</label>
              <div className="relative">
                <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={profile?.mobile || '—'}
                  readOnly
                  disabled
                  className="w-full pl-9 pr-3 py-2.5 bg-slate-100/70 border border-slate-200 rounded-xl text-slate-800 text-sm font-semibold cursor-not-allowed select-none focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
